import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, CircularProgress, alpha, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Chip, IconButton,
  Drawer, Divider, ToggleButton, ToggleButtonGroup, Skeleton,
} from '@mui/material';
import { Close, Assessment, FileDownloadOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, LineChart, Line,
} from 'recharts';
import { distributionAPI } from '../../services/api';
import {
  REGIME_COLORS, exportDrawerCapture, fmtPct, fmtSignedPct, getSectorLabel,
  getTimelineDomain, getTimelineTicks, thSx, tdSx,
} from './shared';

const SectorDetailDrawer = ({ open, onClose, sector, onOpenTicker }) => {
  const { t, i18n } = useTranslation();
  const [members, setMembers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [memberView, setMemberView] = useState('detailed');
  const [exportingImage, setExportingImage] = useState(false);
  const drawerCaptureRef = useRef(null);
  const drawerScrollRef = useRef(null);
  const sectorCode = sector?.sector || null;

  useEffect(() => {
    if (!open || !sectorCode) {
      if (!open) {
        setMembers([]);
        setHistory([]);
      }
      return;
    }

    setLoading(true);
    Promise.all([
      distributionAPI.getSectorMembers(sectorCode).catch(() => ({ data: [] })),
      distributionAPI.getSectorHistory(sectorCode, 30).catch(() => ({ data: [] })),
    ]).then(([membersRes, historyRes]) => {
      setMembers(membersRes.data?.data || membersRes.data || []);
      setHistory(historyRes.data?.data || historyRes.data || []);
    }).finally(() => setLoading(false));
  }, [open, sectorCode]);

  const regime = sector?.sector_regime || 'neutral';
  const color = REGIME_COLORS[regime] || '#64748b';
  const name = getSectorLabel(sector, i18n.language);
  const chartData = [...history].reverse().map((item, index) => ({
    x: index,
    date: item.date?.slice(5),
    score: item.sector_score,
    breadth: Math.round((item.pct_above_ma20 || 0) * 100),
    breakdown: Math.round((item.pct_breakdown || 0) * 100),
    distribution: Math.round((item.pct_distribution || 0) * 100),
    avgRet5: Number(((item.avg_ret_5 || 0) * 100).toFixed(1)),
  }));
  const chartTicks = getTimelineTicks(chartData);
  const chartDomain = getTimelineDomain(chartData);

  const handleExportDrawerImage = useCallback(async () => {
    if (!drawerCaptureRef.current || !drawerScrollRef.current || exportingImage) return;

    try {
      setExportingImage(true);
      const asOfDate = history[0]?.date || new Date().toISOString().slice(0, 10);
      await exportDrawerCapture({
        captureNode: drawerCaptureRef.current,
        scrollNode: drawerScrollRef.current,
        fileName: `${sectorCode || 'sector'}-drawer-${asOfDate}.png`,
      });
    } catch (error) {
      console.error('Failed to export sector drawer image', error);
    } finally {
      setExportingImage(false);
    }
  }, [exportingImage, history, sectorCode]);

  if (!sectorCode || !sector) return null;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 440, md: 480 }, bgcolor: '#fafbfc' } }}>
      <Box ref={drawerCaptureRef} sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: '#fafbfc' }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: 1.5, background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${alpha(color, 0.25)}` }}>
              <Assessment sx={{ fontSize: 22, color: '#fff' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#1e293b' }}>{name || sector.sector}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                <Chip label={t(`distribution.sectorRegime.regimes.${regime}`)} size="small" sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700, bgcolor: alpha(color, 0.1), color }} />
                <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{sector.sector_size} {t('distribution.sectorRegime.members').toLowerCase()}</Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} data-html2canvas-ignore="true" data-export-header-toolbar="true">
            <Tooltip title={exportingImage ? t('common.exportingImage', 'Đang xuất ảnh...') : t('common.exportImage', 'Xuất ảnh')} arrow>
              <span>
                <IconButton
                  onClick={handleExportDrawerImage}
                  disabled={loading || exportingImage}
                  size="small"
                  sx={{
                    border: '1px solid #cbd5e1',
                    bgcolor: '#fff',
                    color: '#475569',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                    '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
                  }}
                >
                  {exportingImage ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlined sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small"><Close /></IconButton>
          </Box>
        </Box>
        <Divider />

        <Box ref={drawerScrollRef} sx={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Skeleton variant="rounded" width="100%" height={100} />
              <Skeleton variant="rounded" width="100%" height={200} />
            </Box>
          ) : (
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', bgcolor: '#e2e8f0', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                {[
                  { label: t('distribution.sectorRegime.score'), value: sector.sector_score?.toFixed(0) || '—', c: color },
                  { label: t('distribution.sectorRegime.breadth'), value: sector.sector_breadth_label || '—', c: sector.sector_breadth_health >= 0.5 ? '#10b981' : '#ef4444' },
                  { label: t('distribution.sectorRegime.pressure'), value: sector.sector_pressure_label || '—', c: (sector.sector_pressure_score || 0) <= 30 ? '#10b981' : '#ef4444' },
                  { label: t('distribution.sectorRegime.aboveMa20'), value: `${((sector.pct_above_ma20 || 0) * 100).toFixed(0)}%`, c: (sector.pct_above_ma20 || 0) >= 0.5 ? '#10b981' : '#ef4444' },
                  { label: t('distribution.sectorRegime.aboveMa50'), value: `${((sector.pct_above_ma50 || 0) * 100).toFixed(0)}%`, c: (sector.pct_above_ma50 || 0) >= 0.5 ? '#10b981' : '#ef4444' },
                  { label: t('distribution.sectorRegime.leaders'), value: `${((sector.pct_leaders || 0) * 100).toFixed(0)}%`, c: '#10b981' },
                  { label: t('distribution.sectorRegime.breakdown'), value: `${((sector.pct_breakdown || 0) * 100).toFixed(0)}%`, c: '#ef4444' },
                  { label: t('distribution.sectorRegime.distributionRate'), value: `${((sector.pct_distribution || 0) * 100).toFixed(0)}%`, c: '#f97316' },
                  { label: t('distribution.sectorRegime.advDec'), value: `${sector.advancers ?? '—'}/${sector.decliners ?? '—'}`, c: '#475569' },
                  { label: t('distribution.sectorRegime.relativeStrength'), value: sector.sector_relative_label || '—', c: '#10b981' },
                  { label: t('distribution.sectorRegime.avgReturn'), value: fmtPct(sector.avg_ret_5), c: (sector.avg_ret_5 || 0) < 0 ? '#ef4444' : '#10b981' },
                  { label: t('distribution.sectorRegime.rs'), value: fmtPct(sector.sector_rs_5), c: (sector.sector_rs_5 || 0) < 0 ? '#ef4444' : '#10b981' },
                  { label: t('distribution.sectorRegime.avgReturn1d'), value: fmtPct(sector.avg_ret_1), c: (sector.avg_ret_1 || 0) < 0 ? '#ef4444' : '#10b981' },
                  { label: t('distribution.sectorRegime.avgReturn10d'), value: fmtPct(sector.avg_ret_10), c: (sector.avg_ret_10 || 0) < 0 ? '#ef4444' : '#10b981' },
                  { label: t('distribution.sectorRegime.avgReturn20d'), value: fmtPct(sector.avg_ret_20), c: (sector.avg_ret_20 || 0) < 0 ? '#ef4444' : '#10b981' },
                  { label: t('distribution.sectorRegime.actionHint'), value: sector.sector_action_hint || '—', c: '#475569' },
                ].map(({ label, value, c: textColor }) => (
                  <Box key={label} sx={{ bgcolor: '#fff', px: 1.5, py: 1 }}>
                    <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: textColor, fontFamily: 'monospace', lineHeight: 1.3 }}>{value}</Typography>
                  </Box>
                ))}
              </Box>

              {chartData.length > 1 && (
                <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>{t('distribution.sectorRegime.historyTitle')}</Typography>
                    <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', mt: 0.6 }}>
                      {[
                        { label: t('distribution.sectorRegime.aboveMa20'), value: `${chartData[chartData.length - 1]?.breadth ?? 0}%`, color: '#3b82f6' },
                        { label: t('distribution.sectorRegime.breakdown'), value: `${chartData[chartData.length - 1]?.breakdown ?? 0}%`, color: '#ef4444' },
                        { label: t('distribution.sectorRegime.distributionRate'), value: `${chartData[chartData.length - 1]?.distribution ?? 0}%`, color: '#f97316' },
                        { label: t('distribution.sectorRegime.avgReturn'), value: `${chartData[chartData.length - 1]?.avgRet5 ?? 0}%`, color: (chartData[chartData.length - 1]?.avgRet5 ?? 0) < 0 ? '#ef4444' : '#10b981' },
                      ].map((item) => (
                        <Typography key={item.label} sx={{ fontSize: '0.62rem', color: item.color, fontWeight: 700, fontFamily: 'monospace' }}>
                          {item.label}: {item.value}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ p: 1.5, height: 180 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis type="number" dataKey="x" domain={chartDomain} ticks={chartTicks} tickFormatter={(value) => chartData[Math.round(value)]?.date || ''} tick={{ fontSize: 9 }} padding={{ left: 0, right: 0 }} allowDecimals={false} />
                        <YAxis yAxisId="score" domain={[0, 'auto']} tick={{ fontSize: 9 }} />
                        <YAxis yAxisId="breadth" orientation="right" domain={[0, 100]} tick={{ fontSize: 9 }} />
                        <RTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                        <Line yAxisId="score" type="monotone" dataKey="score" stroke={color} strokeWidth={2} dot={{ r: 2 }} name={t('distribution.sectorRegime.score')} />
                        <Line yAxisId="breadth" type="monotone" dataKey="breadth" stroke="#3b82f6" strokeWidth={1.5} dot={false} name={t('distribution.sectorRegime.aboveMa20')} />
                        <Line yAxisId="breadth" type="monotone" dataKey="breakdown" stroke="#ef4444" strokeWidth={1.5} dot={false} name={t('distribution.sectorRegime.breakdown')} />
                        <Line yAxisId="breadth" type="monotone" dataKey="distribution" stroke="#f97316" strokeWidth={1.5} dot={false} name={t('distribution.sectorRegime.distributionRate')} />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}

              <Box sx={{ borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.8, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>
                    {t('distribution.sectorRegime.members')} ({members.length})
                  </Typography>
                  <ToggleButtonGroup
                    value={memberView}
                    exclusive
                    size="small"
                    onChange={(_, value) => value && setMemberView(value)}
                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.62rem', fontWeight: 700, py: 0.2, px: 0.8 } }}
                  >
                    <ToggleButton value="compact">{t('distribution.sectorRegime.compactView')}</ToggleButton>
                    <ToggleButton value="detailed">{t('distribution.sectorRegime.detailedView')}</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={thSx}>{t('distribution.table.ticker')}</TableCell>
                        <TableCell sx={thSx} align="right">{t('distribution.sectorRegime.strengthScore')}</TableCell>
                        <TableCell sx={thSx} align="right">{t('distribution.sectorRegime.rankScore')}</TableCell>
                        <TableCell sx={thSx} align="center">MA</TableCell>
                        {memberView === 'detailed' && <TableCell sx={thSx}>{t('distribution.sectorRegime.memberFlags')}</TableCell>}
                        <TableCell sx={thSx} align="right">{memberView === 'detailed' ? 'R 5/10/20' : 'R 5D'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.map((member) => (
                        <TableRow
                          key={member.ticker}
                          onClick={() => onOpenTicker?.({
                            ...member,
                            sector_code: member.sector || sector?.sector,
                            sector_name: sector?.sector_name,
                            sector_name_vi: sector?.sector_name_vi,
                            sector_name_en: sector?.sector_name_en,
                          })}
                          sx={{
                            cursor: onOpenTicker ? 'pointer' : 'default',
                            '&:nth-of-type(even)': { bgcolor: '#fafbfc' },
                            '&:hover': onOpenTicker ? { bgcolor: alpha('#3b82f6', 0.04) } : undefined,
                          }}
                        >
                          <TableCell sx={tdSx}>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#1e293b' }}>{member.ticker}</Typography>
                            {member.company_name && (
                              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', mt: 0.2, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {member.company_name}
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: '0.58rem', color: '#64748b', mt: 0.25, fontFamily: 'monospace' }}>
                              {t('distribution.table.price')}: {member.close ? Number(member.close).toLocaleString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700 }} align="right">
                            {member.stock_strength_score?.toFixed(1) || '—'}
                          </TableCell>
                          <TableCell sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700, color: '#7c3aed' }} align="right">
                            {member.within_sector_rank_score?.toFixed(1) || '—'}
                          </TableCell>
                          <TableCell sx={tdSx} align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.35, flexWrap: 'wrap' }}>
                              {[
                                { label: '20', ok: member.above_ma20 },
                                { label: '50', ok: member.above_ma50 },
                              ].map(({ label, ok }) => (
                                <Typography key={label} sx={{ fontSize: '0.56rem', fontWeight: 700, color: ok ? '#10b981' : '#ef4444' }}>
                                  {ok ? '▲' : '▼'}{label}
                                </Typography>
                              ))}
                            </Box>
                          </TableCell>
                          {memberView === 'detailed' && (
                            <TableCell sx={tdSx}>
                              <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                                {[
                                  member.leadership_like ? { key: 'leader', label: t('distribution.sectorRegime.flagLeader'), color: '#10b981' } : null,
                                  member.distribution_like ? { key: 'distribution', label: t('distribution.sectorRegime.flagDistribution'), color: '#f97316' } : null,
                                  member.strong_distribution_like ? { key: 'strongDistribution', label: t('distribution.sectorRegime.flagStrongDistribution'), color: '#ef4444' } : null,
                                  member.breakdown_like ? { key: 'breakdown', label: t('distribution.sectorRegime.flagBreakdown'), color: '#dc2626' } : null,
                                  member.near_20d_high ? { key: 'nearHigh', label: t('distribution.sectorRegime.flagNearHigh'), color: '#3b82f6' } : null,
                                ].filter(Boolean).map((flag) => (
                                  <Chip key={flag.key} label={flag.label} size="small" sx={{ height: 18, fontSize: '0.52rem', fontWeight: 700, bgcolor: alpha(flag.color, 0.08), color: flag.color }} />
                                ))}
                                {!(member.leadership_like || member.distribution_like || member.strong_distribution_like || member.breakdown_like || member.near_20d_high) && (
                                  <Typography sx={{ fontSize: '0.62rem', color: '#cbd5e1' }}>—</Typography>
                                )}
                              </Box>
                            </TableCell>
                          )}
                          <TableCell sx={{ ...tdSx, fontFamily: 'monospace' }} align="right">
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: (member.ret_5 || 0) < 0 ? '#ef4444' : '#10b981' }}>
                              {fmtSignedPct(member.ret_5)}
                            </Typography>
                            {memberView === 'detailed' && (
                              <>
                                <Typography sx={{ fontSize: '0.58rem', color: (member.ret_10 || 0) < 0 ? '#ef4444' : '#10b981' }}>
                                  {fmtSignedPct(member.ret_10)}
                                </Typography>
                                <Typography sx={{ fontSize: '0.58rem', color: (member.ret_20 || 0) < 0 ? '#ef4444' : '#10b981' }}>
                                  {fmtSignedPct(member.ret_20)}
                                </Typography>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default SectorDetailDrawer;